import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import type { OutlineGroup, OutlineItem } from './outline';
import { S, labelStyle } from './shellTokens';

type Props = {
  groups: OutlineGroup[];
  selectedId: string | null;
  onSelect: (item: OutlineItem) => void;
  onEdit: (item: OutlineItem) => void;
  onDuplicate: (item: OutlineItem) => void;
  onDelete: (item: OutlineItem) => void;
  onReorder: (slotId: string, from: number, to: number) => void;
  /** Viewing an earlier version: sections can be found, not changed. */
  readOnly?: boolean;
  /** False for basic editors: they edit sections but don't add, copy, delete or move them (as on the canvas). */
  canArrange?: boolean;
};

const useStyles = createUseStyles({
  root: { display: 'grid', gap: 18, fontFamily: S.ui, color: S.fg },
  group: { display: 'grid', gap: 4 },
  label: { ...labelStyle, display: 'flex', justifyContent: 'space-between' },
  count: { fontFamily: S.mono, fontSize: 11, fontWeight: 400, letterSpacing: 0, textTransform: 'none' },
  empty: { fontSize: 13, color: S.muted, padding: '4px 0' },
  // The name on the first line (the whole width), its tag and detail under it; a selected row's actions sit on that
  // second line, at the right.
  item: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '16px minmax(0, 1fr) auto',
    gridTemplateAreas: '"handle title title" "handle meta actions"',
    alignItems: 'center',
    columnGap: 6,
    padding: '6px 8px 6px 4px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    border: '1px solid transparent',
    '&:hover': { background: S.chromeHover },
    '&:hover $actions, &:focus-within $actions': { opacity: 1 }
  },
  selected: {
    background: S.field,
    borderColor: S.fieldLine,
    boxShadow: `inset 3px 0 0 ${S.fg}`,
    '&:hover': { background: S.field },
    // The selected section's actions stay in view, on the line under its name, so they never cover it.
    '& $actions': { position: 'static', gridArea: 'actions', justifySelf: 'end', marginTop: 2, padding: 0, background: 'none', opacity: 1 },
    '& $actions::before': { display: 'none' }
  },
  dropBefore: { boxShadow: `0 -2px 0 ${S.fg}` },
  dropAfter: { boxShadow: `0 2px 0 ${S.fg}` },
  handle: { gridArea: 'handle', color: S.muted, cursor: 'grab', fontSize: 14, lineHeight: 1, textAlign: 'center', userSelect: 'none' },
  main: { all: 'unset', gridArea: 'title', minWidth: 0, display: 'grid', cursor: 'pointer', borderRadius: 4, '&:focus-visible': { outline: `2px solid ${S.fg}`, outlineOffset: 2 } },
  // Long names wrap to a second line, then end with "…" (the row's tooltip has the whole name).
  title: { overflow: 'hidden', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontWeight: 500 },
  meta: { gridArea: 'meta', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, marginTop: 2 },
  tag: { flexShrink: 0, fontFamily: S.label, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0 5px', borderRadius: 3, border: `1px solid ${S.fieldLine}`, color: S.muted, lineHeight: '16px' },
  tagAi: { color: S.clayInk, borderColor: S.clay },
  detail: { fontFamily: S.mono, fontSize: 11, color: S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Shown over the row's right end on hover or keyboard focus, so names keep the full width. They cover the row's
  // whole height and fade in at their left edge, so a long name goes under them cleanly, not cut through its letters.
  // Faded out rather than display:none, so screen readers and AI assistants can still find them.
  actions: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    opacity: 0,
    gap: 2,
    padding: '0 4px 0 2px',
    borderRadius: '0 5px 5px 0',
    background: S.chromeHover,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: '100%',
      width: 20,
      background: `linear-gradient(to right, transparent, ${S.chromeHover})`,
      pointerEvents: 'none'
    }
  },
  action: {
    all: 'unset',
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 17 },
    '&:hover, &:focus-visible': { background: S.line, color: S.fg },
    '&[disabled]': { opacity: 0.35, cursor: 'default', background: 'none' }
  }
});

const OutlinePanel: React.FC<Props> = ({ groups, selectedId, onSelect, onEdit, onDuplicate, onDelete, onReorder, readOnly, canArrange = true }) => {
  const c = useStyles();
  const movable = !readOnly && canArrange;
  const [drag, setDrag] = useState<{ slotId: string; index: number } | null>(null);
  const [over, setOver] = useState<{ slotId: string; index: number; after: boolean } | null>(null);

  const drop = (slotId: string) => {
    if (drag && over && drag.slotId === slotId && over.slotId === slotId) {
      let to = over.index + (over.after ? 1 : 0);
      if (to > drag.index) to -= 1; // the dragged one leaves its old place first
      if (to !== drag.index) onReorder(slotId, drag.index, to);
    }
    setDrag(null);
    setOver(null);
  };

  return (
    <div className={c.root} data-testid="outline-panel">
      {groups.map(group => (
        <section key={group.slotId} className={c.group} aria-label={group.label}>
          <div className={c.label}><span>{group.label}</span><span className={c.count}>{group.items.length}</span></div>
          {!group.items.length && <div className={c.empty}>Empty</div>}
          {group.items.map(item => {
            const dropClass = over && drag && over.slotId === group.slotId && over.index === item.index && drag.index !== item.index
              ? (over.after ? c.dropAfter : c.dropBefore)
              : '';
            return (
              // Not a button itself: buttons inside a role=button are hidden from screen readers and AI
              // assistants, so the row's title is the select button and the actions sit beside it.
              <div
                key={item.id}
                data-outline-item={item.id}
                className={[
                  c.item,
                  item.id === selectedId ? c.selected : '',
                  dropClass
                ].join(' ')}
                draggable={movable}
                onDragStart={(e) => { if (!movable) return; setDrag({ slotId: group.slotId, index: item.index }); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', item.id); }}
                onDragOver={(e) => {
                  if (!drag || drag.slotId !== group.slotId) return;
                  e.preventDefault();
                  const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setOver({ slotId: group.slotId, index: item.index, after: e.clientY > box.top + box.height / 2 });
                }}
                onDrop={(e) => { e.preventDefault(); drop(group.slotId); }}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                onClick={() => onSelect(item)}
                title={item.children ? `${item.title} · ${item.children} inside` : item.title}
              >
                <span className={c.handle} aria-hidden>{movable ? '⋮⋮' : ''}</span>
                <button type="button" className={c.main} aria-pressed={item.id === selectedId} aria-label={`Select ${item.title}${item.tag ? ` (${item.tag})` : ''}`} data-outline-select={item.id}>
                  <span className={c.title}>{item.title}</span>
                </button>
                {(item.tag || item.detail) && (
                  <span className={c.meta}>
                    {item.tag && <span className={`${c.tag} ${item.tag === 'AI' ? c.tagAi : ''}`}>{item.tag}</span>}
                    {item.detail && <span className={c.detail}>{item.detail}</span>}
                  </span>
                )}
                {!readOnly && <span className={c.actions}>
                  <button type="button" className={c.action} aria-label={`Edit ${item.title}`} title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(item); }}><i className="material-icons" aria-hidden>tune</i></button>
                  {movable && <button type="button" className={c.action} aria-label={`Move ${item.title} up`} title="Move up" disabled={item.index === 0} onClick={(e) => { e.stopPropagation(); onReorder(group.slotId, item.index, item.index - 1); }}><i className="material-icons" aria-hidden>arrow_upward</i></button>}
                  {movable && <button type="button" className={c.action} aria-label={`Move ${item.title} down`} title="Move down" disabled={item.index === group.items.length - 1} onClick={(e) => { e.stopPropagation(); onReorder(group.slotId, item.index, item.index + 1); }}><i className="material-icons" aria-hidden>arrow_downward</i></button>}
                  {canArrange && <button type="button" className={c.action} aria-label={`Duplicate ${item.title}`} title="Duplicate" onClick={(e) => { e.stopPropagation(); onDuplicate(item); }}><i className="material-icons" aria-hidden>content_copy</i></button>}
                  {canArrange && <button type="button" className={c.action} aria-label={`Delete ${item.title}`} title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(item); }}><i className="material-icons" aria-hidden>delete_outline</i></button>}
                </span>}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
};

export default OutlinePanel;
